import {
  childSampleNumber,
  newSample,
  Sample,
  updateSampleDocument,
  updateSampleLab,
  updateSampleNumber,
} from "@/services/backend/samples/types/sample";
import { GrowthMedium } from "@/services/backend/samples/types/growth-medium";
import { Kind } from "@/services/backend/samples/types/kind";
import { v4 } from "uuid";
import { UUID } from "@bitory-gmbh/nidavellir";
import { EntityId } from "@/shared/types/entity-id";
import { Namespace } from "@/shared/types/namespace";
import { MoldDifferentiation } from "@/services/backend/samples/types/mold-differentiation";

export interface SurfaceMicrobialSampleSet {
  id: string;
  companyId: UUID;
  documentId: EntityId;
  documentNamespace: Namespace | null;
  labId: EntityId;
  number: string;
  location: string;
  spot: string;
  comment: string;
  sampleTakers: string;
  value: string;
  unit: string;
  /** The germ count from which on the set is judged as a warning. */
  warningFrom: number;
  /**
   * The germ count above which the set is judged as critical; 0 means no
   * thresholds are set. Both are copied from the base data on creation.
   */
  criticalAbove: number;
  caso: SurfaceMicrobialSample | null;
  malt: SurfaceMicrobialSample | null;
  dg18: SurfaceMicrobialSample | null;
  moldDifferentiation: MoldDifferentiation[];
}

export function updateDocument(
  set: SurfaceMicrobialSampleSet,
  id: EntityId,
  namespace: Namespace,
): SurfaceMicrobialSampleSet {
  return {
    ...set,
    documentId: id,
    documentNamespace: namespace,
    caso: set.caso ? updateSampleDocument(set.caso, id, namespace) : null,
    malt: set.malt ? updateSampleDocument(set.malt, id, namespace) : null,
    dg18: set.dg18 ? updateSampleDocument(set.dg18, id, namespace) : null,
  };
}

export function updateSurfaceMicrobialSetNumber(
  set: SurfaceMicrobialSampleSet,
  number: string,
): SurfaceMicrobialSampleSet {
  return {
    ...set,
    number,
    caso: set.caso
      ? updateSampleNumber(
          set.caso,
          childSampleNumber(number, GrowthMedium.CASO),
        )
      : null,
    malt: set.malt
      ? updateSampleNumber(
          set.malt,
          childSampleNumber(number, GrowthMedium.MALT),
        )
      : null,
    dg18: set.dg18
      ? updateSampleNumber(
          set.dg18,
          childSampleNumber(number, GrowthMedium.DG18),
        )
      : null,
  };
}

export function updateCreatedAtInSurfaceSet(
  sample: SurfaceMicrobialSampleSet,
  createdAt: string | null,
): SurfaceMicrobialSampleSet {
  return {
    ...sample,
    caso: sample.caso ? { ...sample.caso, createdAt } : sample.caso,
    malt: sample.malt ? { ...sample.malt, createdAt } : sample.malt,
    dg18: sample.dg18 ? { ...sample.dg18, createdAt } : sample.dg18,
  };
}

export function updateSurfaceMicrobialSetLab(
  set: SurfaceMicrobialSampleSet,
  labId: EntityId,
): SurfaceMicrobialSampleSet {
  return {
    ...set,
    labId,
    caso: set.caso ? updateSampleLab(set.caso, labId) : null,
    malt: set.malt ? updateSampleLab(set.malt, labId) : null,
    dg18: set.dg18 ? updateSampleLab(set.dg18, labId) : null,
  };
}

export function updateSurfaceMicrobialSetComment(
  set: SurfaceMicrobialSampleSet,
  comment: string,
): SurfaceMicrobialSampleSet {
  return {
    ...set,
    comment,
  };
}

export function updateSurfaceMicrobialSetCaso(
  set: SurfaceMicrobialSampleSet,
  sample: SurfaceMicrobialSample,
): SurfaceMicrobialSampleSet {
  if (sample.growthMedium !== GrowthMedium.CASO) {
    return set;
  }

  return {
    ...set,
    caso: sample,
  };
}

export function updateSurfaceMicrobialSetMalt(
  set: SurfaceMicrobialSampleSet,
  sample: SurfaceMicrobialSample,
): SurfaceMicrobialSampleSet {
  if (sample.growthMedium !== GrowthMedium.MALT) {
    return set;
  }

  return {
    ...set,
    malt: sample,
  };
}

export function updateSurfaceMicrobialSetDg18(
  set: SurfaceMicrobialSampleSet,
  sample: SurfaceMicrobialSample,
): SurfaceMicrobialSampleSet {
  if (sample.growthMedium !== GrowthMedium.DG18) {
    return set;
  }

  return {
    ...set,
    dg18: sample,
  };
}

/**
 * Adds the sample of the growth medium to the set, if it is missing. It
 * takes the unit of the samples already in the set, which the base data
 * set when the set was created.
 */
export function activateSurfaceMicrobialSetSample(
  set: SurfaceMicrobialSampleSet,
  growthMedium: GrowthMedium,
): SurfaceMicrobialSampleSet {
  const sibling = set.caso ?? set.malt ?? set.dg18;
  const unit = sibling?.unit ?? set.unit;

  switch (growthMedium) {
    case GrowthMedium.CASO:
      return {
        ...set,
        caso: set.caso ?? newSurfaceMicrobialSample(set, growthMedium, unit),
      };
    case GrowthMedium.MALT:
      return {
        ...set,
        malt: set.malt ?? newSurfaceMicrobialSample(set, growthMedium, unit),
      };
    case GrowthMedium.DG18:
      return {
        ...set,
        dg18: set.dg18 ?? newSurfaceMicrobialSample(set, growthMedium, unit),
      };
    default:
      return set;
  }
}

export function deactivateSurfaceMicrobialSetSample(
  set: SurfaceMicrobialSampleSet,
  growthMedium: GrowthMedium,
): SurfaceMicrobialSampleSet {
  switch (growthMedium) {
    case GrowthMedium.CASO:
      return {
        ...set,
        caso: null,
      };
    case GrowthMedium.MALT:
      return {
        ...set,
        malt: null,
      };
    case GrowthMedium.DG18:
      return {
        ...set,
        dg18: null,
      };
    default:
      return set;
  }
}

export function surfaceMicrobialSampleIsActivated(
  set: SurfaceMicrobialSampleSet,
  growthMedium: GrowthMedium,
): boolean {
  switch (growthMedium) {
    case GrowthMedium.CASO:
      return !!set.caso;
    case GrowthMedium.MALT:
      return !!set.malt;
    case GrowthMedium.DG18:
      return !!set.dg18;
    default:
      return false;
  }
}

export interface SurfaceMicrobialSample extends Sample {
  growthMedium: GrowthMedium;
  value: string;
  unit: string;
}

/**
 * Builds a sample of the set, sharing its company, document, location,
 * spot and lab. Mirrors `SurfaceMicrobialSet.newSample` in the backend.
 */
function newSurfaceMicrobialSample(
  set: SurfaceMicrobialSampleSet,
  growthMedium: GrowthMedium,
  unit: string,
): SurfaceMicrobialSample {
  const sample = newSample(v4(), Kind.SurfaceMicrobial, {
    companyId: set.companyId,
    documentId: set.documentId,
    documentNamespace: set.documentNamespace,
    number: childSampleNumber(set.number, growthMedium),
    location: set.location,
    spot: set.spot,
  });

  return {
    ...sample,
    labId: set.labId,
    growthMedium,
    value: "",
    unit,
  };
}
